import { apiFetch } from '@/lib/api/client';
import type {
  GuardianStudentView,
  InviteGuardianInput,
  InviteGuardianResult,
  StudentGuardianView,
} from '@/features/guardian/types';

/**
 * `POST /students/:studentId/guardians` (ADR-025's Addendum, Decision E).
 * Tenant-scoped (`tenantScoped: true`, ADR-026 Addendum AO-2) - the
 * shared HTTP client attaches `X-Organization-Id` from the current
 * Active Organization automatically.
 */
export async function inviteGuardian(
  studentId: string,
  input: InviteGuardianInput,
): Promise<InviteGuardianResult> {
  return apiFetch<InviteGuardianResult>(`/students/${studentId}/guardians`, {
    method: 'POST',
    body: input,
    tenantScoped: true,
  });
}

/** `GET /students/:studentId/guardians`. Tenant-scoped (AO-2). */
export async function listStudentGuardians(
  studentId: string,
): Promise<readonly StudentGuardianView[]> {
  return apiFetch<readonly StudentGuardianView[]>(`/students/${studentId}/guardians`, {
    tenantScoped: true,
  });
}

/**
 * `DELETE /students/:studentId/guardians/:guardianId`. The backend itself
 * resolves administrative-revoke vs. self-revoke from the caller's
 * authorization context - the frontend never decides this
 * (`guardian.controller.ts`'s own doc comment: "this never exposes the
 * administrative path to a caller who does not actually hold
 * students:manage").
 */
export async function revokeGuardian(studentId: string, guardianId: string): Promise<void> {
  await apiFetch<unknown>(`/students/${studentId}/guardians/${guardianId}`, {
    method: 'DELETE',
    tenantScoped: true,
  });
}

/**
 * `POST /guardian-invitations/:token/accept`. Deliberately takes no
 * organization id and is NOT `tenantScoped` - the backend derives the
 * organization entirely from the token's linked relationship (ADR-025's
 * Addendum, Decision K; ADR-026 Addendum, AO-12), so this must keep
 * working even with no Active Organization selected.
 */
export async function acceptGuardianInvitation(token: string): Promise<StudentGuardianView> {
  return apiFetch<StudentGuardianView>(`/guardian-invitations/${token}/accept`, {
    method: 'POST',
  });
}

/** `GET /guardians/me/students`. Tenant-scoped (AO-2, AO-10). */
export async function listMyGuardianStudents(): Promise<readonly GuardianStudentView[]> {
  return apiFetch<readonly GuardianStudentView[]>('/guardians/me/students', {
    tenantScoped: true,
  });
}
